/**
 * Bull / bear ROAS scenario adjustment.
 *
 * A scenario shifts the cumulative ROAS vector used for revenue and repayments
 * (Hill spend vectors when fit, otherwise the generic manual vector) up for a
 * bull case or down for a bear case, across the whole vector so every period is
 * affected — not just the first.
 *
 * Because the vector is CUMULATIVE ROAS, the shift is applied to each period's
 * marginal (period-over-period increment):
 *  - percent: scale each cumulative value by (1 ± value%), which scales every
 *    period's increment proportionally.
 *  - absolute: add / subtract `value` ROAS points to each period's increment, so
 *    every period earns more (bull) or less (bear). Increments are floored at 0
 *    so the cumulative curve stays non-decreasing and >= 0.
 *
 * Example (5-period base [5, 14, 28, 46, 62], bull absolute +5):
 *   increments [5, 9, 14, 18, 16] -> [10, 14, 19, 23, 21]
 *   cumulative -> [10, 24, 43, 66, 87]
 */

export const SCENARIO_ADJUSTMENT_MODE = {
  ABSOLUTE: 'absolute',
  PERCENT: 'percent',
};

/** True when the adjustment leaves every value unchanged. */
export function isNoopScenarioAdjustment(adjustment) {
  if (!adjustment) {
    return true;
  }
  const magnitude = Math.max(0, Number(adjustment.value) || 0);
  return magnitude <= 0;
}

/**
 * Adjust a whole cumulative ROAS vector for a bull / bear scenario.
 * Returns the input reference unchanged for a no-op or non-array input.
 */
export function adjustCumulativeRoasVector(vector, adjustment) {
  if (!Array.isArray(vector) || isNoopScenarioAdjustment(adjustment)) {
    return vector;
  }
  const magnitude = Math.max(0, Number(adjustment.value) || 0);
  const sign = adjustment.direction < 0 ? -1 : 1;

  if (adjustment.mode === SCENARIO_ADJUSTMENT_MODE.PERCENT) {
    const factor = Math.max(0, 1 + (sign * magnitude) / 100);
    return vector.map((value) => Math.max(0, (Number(value) || 0) * factor));
  }

  const delta = sign * magnitude;
  let prevOriginal = 0;
  let cumulative = 0;
  return vector.map((value) => {
    const original = Number(value) || 0;
    const marginal = original - prevOriginal;
    prevOriginal = original;
    const adjustedMarginal = Math.max(0, marginal + delta);
    cumulative += adjustedMarginal;
    return Math.max(0, cumulative);
  });
}

/**
 * Build a function that maps a cumulative ROAS vector to its adjusted version.
 * Returns the identity function (same reference in / out) when the adjustment is
 * a no-op, so callers can cheaply detect "unchanged".
 */
export function makeRoasScenarioAdjuster(adjustment) {
  if (isNoopScenarioAdjustment(adjustment)) {
    return (vector) => vector;
  }
  return (vector) => adjustCumulativeRoasVector(vector, adjustment);
}
